import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import CheckoutForm from './components/CheckoutForm';
import DonationForm from './components/DonationForm';
import CookieBanner from './components/CookieBanner';
import logoImage from './assets/images/logo_cemcat.png';
import { api } from './services/api';
import './App.css';
import './components/Footer.css';

// Get the publishable key from environment variables
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
// Initialize Stripe
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

// Translations for the app
const translations = {
  ca: {
    title: "Fes una donació",
    subtitle: "El teu donatiu dona suport a la investigació al CEMCAT.",
    step1: "Dades",
    step2: "Pagament",
    thanks: "Gràcies pel teu suport a la investigació al CEMCAT.",
    footer: "La recerca i la docència són pilars bàsics del Centre d'Esclerosi Múltiple de Catalunya (Cemcat) i milloren en l'atenció que es dóna al pacient."
  },
  es: {
    title: "Haz una donación",
    subtitle: "Tu donativo apoya la investigación en el CEMCAT.",
    step1: "Datos",
    step2: "Pago",
    thanks: "Gracias por tu apoyo a la investigación en el CEMCAT.",
    footer: "La investigación y la docencia son pilares básicos del Centro de Esclerosis Múltiple de Cataluña (Cemcat) y mejoran la atención que se da al paciente."
  },
  en: {
    title: "Make a donation",
    subtitle: "Your donation supports research at CEMCAT.",
    step1: "Information",
    step2: "Payment",
    thanks: "Thank you for your support of research at CEMCAT.",
    footer: "Research and teaching are basic pillars of the Multiple Sclerosis Center of Catalonia (Cemcat) and improve the care given to patients."
  }
};

// Define the form data type
interface FormData {
  fullName: string;
  email: string;
  idNumber: string;
  address: string;
  phone: string;
  donationAmount: string;
  termsAccepted: boolean;
  language: string;
}

const App: React.FC = () => {
  const [clientSecret, setClientSecret] = useState<string>('');
  const [showPaymentElement, setShowPaymentElement] = useState<boolean>(false);
  const [serverError, setServerError] = useState<string | undefined>(undefined);
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    idNumber: '',
    address: '',
    phone: '',
    donationAmount: '50',
    termsAccepted: false,
    language: 'ca'
  });

  // Get the current language from URL path
  useEffect(() => {
    const path = window.location.pathname;
    let language = 'ca'; // Default to Catalan
    
    if (path.startsWith('/es')) {
      language = 'es';
    } else if (path.startsWith('/en')) {
      language = 'en';
    } else if (path === '/' || path.startsWith('/ca')) {
      language = 'ca';
    }
    
    // Only update language if different from current
    if (language !== formData.language) {
      setFormData(prev => ({ ...prev, language }));
    }
  }, []);

  // Get the current language for translations
  const currentLang = formData.language || 'ca';
  const t = translations[currentLang as keyof typeof translations];

  const handleFormChange = (newData: Partial<FormData>) => {
    setFormData({ ...formData, ...newData });
    // Clear server error when form data changes
    if (serverError) {
      setServerError(undefined);
    }
    
    // If language changed, update the URL
    if (newData.language && newData.language !== formData.language) {
      const newPath = `/${newData.language}`;
      window.history.pushState({}, '', newPath);
    }
  };

  const handleLanguageChange = (language: string) => {
    handleFormChange({ language });
  };

  const handleProceedToPayment = async () => {
    // Clear any previous errors
    setServerError(undefined);
    
    // Create PaymentIntent when user proceeds to payment
    try {
      // First get the current donation amount
      const currentAmount = formData.donationAmount === 'custom' ? '5' : formData.donationAmount;
      
      const data = await api.post('/create-payment-intent', { 
        amount: currentAmount ? parseInt(currentAmount) : 5,
        currency: 'eur',
        email: formData.email,
        // Send all form data
        fullName: formData.fullName,
        idNumber: formData.idNumber,
        address: formData.address,
        phone: formData.phone
      });
      
      if (data.error) {
        // Handle server-side error based on language
        setServerError(data.error);
        return;
      }
      
      setClientSecret(data.clientSecret);
      setShowPaymentElement(true);
      
      // Scroll to the payment form after it renders
      setTimeout(() => {
        const progressElement = document.querySelector('.payment-progress');
        window.scrollTo({
          top: progressElement ? (progressElement as HTMLElement).offsetTop : 0,
          behavior: 'smooth'
        });
      }, 100);
    } catch (error) {
      console.error('Error creating payment intent:', error);
      
      // Show error message based on language
      const errorMessages = {
        ca: 'Hi ha hagut un error en processar la sol·licitud. Si us plau, intenta-ho de nou més tard.',
        es: 'Ha habido un error al procesar la solicitud. Por favor, inténtalo de nuevo más tarde.',
        en: 'There was an error processing your request. Please try again later.'
      };
      
      setServerError(errorMessages[currentLang as keyof typeof errorMessages]);
    }
  };

  const handleGoBack = () => {
    setShowPaymentElement(false);
  };

  return (
    <div className="App">
      <div className="donation-container">
        <div className="header-container">
          <img src={logoImage} alt="CEMCAT Logo" className="cemcat-logo" />
          <div className="language-selector top-right">
            <button 
              type="button" 
              className={`language-btn ${currentLang === 'ca' ? 'active' : ''}`}
              onClick={() => handleLanguageChange('ca')}
            >
              Català
            </button>
            <button 
              type="button" 
              className={`language-btn ${currentLang === 'es' ? 'active' : ''}`}
              onClick={() => handleLanguageChange('es')}
            >
              Español
            </button>
            <button 
              type="button" 
              className={`language-btn ${currentLang === 'en' ? 'active' : ''}`}
              onClick={() => handleLanguageChange('en')}
            >
              English
            </button>
          </div>
        </div>
        
        <div className="main-title">
          <h1>{t.title}</h1>
          <p className="donation-subheader">{t.subtitle}</p>
        </div>
        
        {!showPaymentElement ? (
          <div className="form-content">
            <div className="form-left">
              <DonationForm 
                formData={formData}
                onFormChange={handleFormChange}
                onSubmit={handleProceedToPayment}
                serverError={serverError}
              />
            </div>
          </div>
        ) : clientSecret && (
          <>
            <div className="payment-progress">
              <div className="progress-step completed">
                <div className="step-number">1</div>
                <div className="step-label">{t.step1}</div>
              </div>
              <div className="progress-line"></div>
              <div className="progress-step active">
                <div className="step-number">2</div>
                <div className="step-label">{t.step2}</div>
              </div>
            </div>
            <div className="form-content">
              <div className="form-left">
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                  <CheckoutForm formData={formData} onGoBack={handleGoBack} />
                </Elements>
              </div>
            </div>
          </>
        )}
        <div className="footer">
          <p>{t.thanks}</p>
        </div>

        <footer className="site-footer">
          <div className="footer-top">
            <p>{t.footer}</p>
          </div>
          
          <div className="footer-middle">
            <div className="footer-logo">
              <img src={logoImage} alt="CEMCAT Logo" />
            </div>
            
            <div className="footer-contact">
              <h3>
                {currentLang === 'ca' ? 'Estem en contacte' : 
                 currentLang === 'es' ? 'Estamos en contacto' : 
                 'Contact us'}
              </h3>
              <div className="contact-details">
                <div className="contact-item">
                  <span className="icon">📞</span>
                  <span>+34 93 175 15 59</span>
                </div>
                <div className="contact-item">
                  <span className="icon">✉️</span>
                  <span>fundacio.cemcat@cem-cat.org</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="footer-bottom">
            <div className="legal-links">
              <a href="https://www.cem-cat.org/ca/pol%C3%ADtica-de-privacitat" target="_blank" rel="noopener noreferrer">
                {currentLang === 'ca' ? 'Política de privacitat' : 
                 currentLang === 'es' ? 'Política de privacidad' : 
                 'Privacy policy'}
              </a>
              <span className="separator">·</span>
              <a href="https://www.cem-cat.org/ca/av%C3%ADs-legal" target="_blank" rel="noopener noreferrer">
                {currentLang === 'ca' ? 'Avís legal' : 
                 currentLang === 'es' ? 'Aviso legal' : 
                 'Legal notice'}
              </a>
              <span className="separator">·</span>
              <a href="https://www.cem-cat.org/ca/pol%C3%ADtica-de-cookies" target="_blank" rel="noopener noreferrer">
                {currentLang === 'ca' ? 'Política de cookies' : 
                 currentLang === 'es' ? 'Política de cookies' : 
                 'Cookie policy'}
              </a>
              <span className="separator">·</span>
              <a href="https://www.cem-cat.org/ca/condicions-generals-de-contractaci%C3%B3" target="_blank" rel="noopener noreferrer">
                {currentLang === 'ca' ? 'Condicions generals de contractació' : 
                 currentLang === 'es' ? 'Condiciones generales de contratación' : 
                 'General terms and conditions'}
              </a>
              <span className="separator">·</span>
              <a href="https://www.cem-cat.org/ca/pol%C3%ADtica-de-privacitat-xarxes-socials" target="_blank" rel="noopener noreferrer">
                {currentLang === 'ca' ? 'Política de xarxes socials' : 
                 currentLang === 'es' ? 'Política de redes sociales' : 
                 'Social media policy'}
              </a>
            </div>
            <div className="copyright">
              © {new Date().getFullYear()} Centre d'Esclerosi Múltiple de Catalunya
            </div>
          </div>
        </footer>
      </div>
      <CookieBanner />
    </div>
  );
};

export default App; 
